/**
 * Client Review Store
 *
 * Manages ALL active reviews for a company in the client-side review detail view.
 * Items from multiple deliverables in `client_review` stage are merged together.
 * Each item is tagged with `_reviewId` so actions route to the correct review.
 * Admin/super comments are filtered out for client users.
 */
import { defineStore } from 'pinia';
import { ref, shallowRef, computed } from 'vue';
import { doc, getDoc, getDocs, collection, onSnapshot } from 'firebase/firestore';
import { db } from '@/js/firebase';
import { reviewService } from '@/services/reviewService';
import { useUserAuthStore } from '@/stores/userAuthStore';
import { useViewModeStore } from '@/stores/viewModeStore';
import { logger } from '@/services/loggerService';
import { REVIEW_ITEM_STATUS, FINALIZATION_STATUS, isActionableStatus } from '@/utils/reviewUtils';
import { episodeDisplayTitle } from '@/utils/projectUtils';

export const useClientReviewStore = defineStore('clientReview', () => {
  // ─── External Stores ───────────────────────────────────────
  const userAuthStore = useUserAuthStore();
  const viewModeStore = useViewModeStore();

  // ─── State ───────────────────────────────────────────────

  /** All qualifying review docs, keyed by reviewId. shallowRef: replaced wholesale. */
  const reviews = shallowRef({});

  /** Computed: resolve the review for the currently active item */
  const review = computed(() => {
    const rid = activeItem.value?._reviewId;
    if (rid && reviews.value[rid]) return reviews.value[rid];
    // Fallback: first review
    const keys = Object.keys(reviews.value);
    return keys.length > 0 ? reviews.value[keys[0]] : null;
  });

  /** shallowRef: large collection replaced wholesale via _recomputeItems */
  const items = shallowRef({});
  /** shallowRef: large collection replaced wholesale via _recomputeComments */
  const comments = shallowRef([]);
  const loading = ref(false);
  const initialized = ref(false);
  const error = ref(null);
  const activeItemId = ref(null);
  const activeVersionIds = ref({});
  const currentCompanyId = ref(null);
  const clientFacingVersionIds = ref(new Set());
  const deliverableStage = ref(null);
  const reviewMeta = ref({});
  /** Podcast episodes at client_review — surfaced as a hub banner, reviewed on the episode page. */
  const pendingEpisodeReviews = ref([]);
  // Sent back for changes and now with the editor (out of client_review). Display-only rows.
  const withEditorReviews = ref([]);
  const initDebug = ref({ steps: [], user: null, route: null, reviews: null, deliverables: null, access: null, items: null, exitReason: null, error: null });

  // Internal per-review maps for merging
  const _reviewItems = {};
  const _reviewComments = {};
  const _unsubDeliverables = {};
  // Reviews pinned by an actively-mounted single-review view (the episode review
  // panel). The hub's multi-review reconcile excludes primary-episode deliverables
  // from its target set, so without this it would evict the panel's own review out
  // from under it. Protected ids are skipped by the reconcile teardown.
  const _protectedReviewIds = new Set();

  function _debugStep(label, data = null) {
    initDebug.value = {
      ...initDebug.value,
      steps: [...(initDebug.value.steps || []), { label, data, time: new Date().toISOString() }]
    };
  }

  /**
   * Reliably detect admin/super even during early init when systemRole may not be populated yet.
   * Impersonating staff get the exact client view (stage gate, client-facing versions, comments).
   */
  function _isAdminOrSuper(user) {
    if (viewModeStore.isImpersonating) return false;
    const role = user?.systemRole;
    const type = user?.type;
    return role === 'super' || role === 'admin' || type === 'super' || type === 'admin';
  }

  // ─── Getters ─────────────────────────────────────────────

  const sortedItems = computed(() =>
    Object.values(items.value).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
  );

  const actionableItems = computed(() => {
    // Default: admin/super (or impersonating admin) sees all items regardless of status
    if (_isAdminOrSuper(userAuthStore.user)) return sortedItems.value;
    return sortedItems.value.filter(item => isActionableStatus(item.clientStatus));
  });

  const allItemsActioned = computed(() =>
    sortedItems.value.length > 0 && actionableItems.value.length === 0
  );

  const activeItem = computed(() => {
    if (!activeItemId.value) return null;
    return items.value[activeItemId.value] || null;
  });

  const activeVersion = computed(() => {
    const item = activeItem.value;
    if (!item) return null;
    const versionId = activeVersionIds.value[item.id];
    if (versionId) {
      return item.versions?.find(v => v.id === versionId) || item.versions?.[0] || null;
    }
    return item.versions?.[0] || null;
  });

  const itemComments = computed(() => {
    return (itemId, versionId) => {
      return comments.value.filter(c =>
        c.itemId === itemId && c.versionId === versionId
      );
    };
  });

  const unresolvedCommentCount = computed(() =>
    comments.value.filter(c => !c.parentId && !c.resolved).length
  );

  const reviewProgress = computed(() => {
    const allItems = Object.values(items.value);
    return {
      approved: allItems.filter(i => i.clientStatus === REVIEW_ITEM_STATUS.APPROVED).length,
      pending: allItems.filter(i => i.clientStatus === REVIEW_ITEM_STATUS.PENDING_REVIEW).length,
      changesRequested: allItems.filter(i => i.clientStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED).length,
      total: allItems.length
    };
  });

  /** First available reviewId — stable fallback for components */
  const firstReviewId = computed(() => {
    const keys = Object.keys(reviews.value);
    return keys.length > 0 ? keys[0] : null;
  });

  // ─── Internal helpers ──────────────────────────────────────

  /** Resolve reviewId from an item id */
  function _reviewIdForItem(itemId) {
    return items.value[itemId]?._reviewId || null;
  }

  /** Resolve reviewId from a comment id */
  function _reviewIdForComment(commentId) {
    const comment = comments.value.find(c => c.id === commentId);
    if (comment?._reviewId) return comment._reviewId;
    // Fallback: find via the comment's itemId
    if (comment?.itemId) return _reviewIdForItem(comment.itemId);
    return null;
  }

  /** Recompute merged items from all per-review item maps */
  function _recomputeItems() {
    const merged = {};
    for (const [reviewId, reviewItems] of Object.entries(_reviewItems)) {
      for (const [id, item] of Object.entries(reviewItems)) {
        merged[id] = { ...item, _reviewId: reviewId };
      }
    }
    items.value = merged;
  }

  /** Recompute merged comments from all per-review comment arrays */
  function _recomputeComments() {
    const merged = [];
    for (const [reviewId, reviewComments] of Object.entries(_reviewComments)) {
      for (const c of reviewComments) {
        merged.push({ ...c, _reviewId: reviewId });
      }
    }
    comments.value = merged;
  }

  /** Remove a single review's data (when its deliverable leaves client_review) */
  function _removeReview(reviewId) {
    // Unsubscribe review-specific listeners
    reviewService.unsubscribe(`review_${reviewId}`);
    reviewService.unsubscribe(`items_${reviewId}`);
    reviewService.unsubscribe(`comments_${reviewId}`);

    delete _reviewItems[reviewId];
    delete _reviewComments[reviewId];
    const newReviews = { ...reviews.value };
    delete newReviews[reviewId];
    reviews.value = newReviews;

    if (_unsubDeliverables[reviewId]) {
      _unsubDeliverables[reviewId]();
      delete _unsubDeliverables[reviewId];
    }

    _recomputeItems();
    _recomputeComments();

    // If the active item was in this review, select a different one
    if (activeItemId.value && !items.value[activeItemId.value]) {
      const remaining = Object.values(items.value).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      activeItemId.value = remaining[0]?.id || null;
    }
  }

  // ─── Actions ─────────────────────────────────────────────

  async function init(companyId, reviewId) {
    const debugBase = { steps: [], user: null, route: null, reviews: null, deliverables: null, access: null, items: null, exitReason: null, error: null };
    initDebug.value = debugBase;

    // Capture route context
    initDebug.value.route = { companyId: companyId || null, reviewIdParam: reviewId || null };
    _debugStep('init() called', { companyId, reviewId });

    // Capture user context
    const user = userAuthStore.user;
    if (user) {
      const companyEntry = user.companies?.find(c => c.companyId === companyId);
      initDebug.value.user = {
        id: user.id,
        name: user.name || user.profile?.displayName || null,
        email: user.email,
        systemRole: user.systemRole,
        activeCompanyId: user.activeCompanyId || user.companyId,
        companyCount: user.companies?.length || 0,
        matchingCompany: companyEntry ? { companyId: companyEntry.companyId, companyName: companyEntry.companyName, role: companyEntry.role, permissionLevel: companyEntry.permissionLevel, status: companyEntry.status } : null,
      };
    }
    _debugStep('User context captured');

    if (!companyId) {
      initDebug.value.exitReason = 'No companyId — route.params.id is missing or empty';
      _debugStep('EXIT: no companyId');
      return;
    }

    // If switching companies, reset
    if (currentCompanyId.value && currentCompanyId.value !== companyId) {
      reset();
    }

    try {
      loading.value = true;
      error.value = null;
      currentCompanyId.value = companyId;

      // ── Single review by ID (e.g. direct link) ──
      if (reviewId) {
        _debugStep('Using reviewId from param', { reviewId });
        const reviewData = await reviewService.getReview(reviewId);
        if (!reviewData) {
          initDebug.value.exitReason = `Review "${reviewId}" not found`;
          _debugStep('EXIT: review not found');
          initialized.value = true;
          loading.value = false;
          return;
        }

        // Build reviewMeta for single review
        const deliverableId = reviewData.deliverableId || reviewData.id;
        try {
          const delSnap = await getDoc(doc(db, 'deliverables', deliverableId));
          const delData = delSnap.data();
          const metaEntry = {
            deliverableName: delData?.title || '',
            projectName: '',
          };
          if (reviewData.projectId) {
            const projSnap = await getDoc(doc(db, 'projects', reviewData.projectId));
            const projData = projSnap.data();
            metaEntry.projectName = projData?.name || '';
            if (delData?.isPrimaryEpisodeDeliverable && projData?.type === 'episode') {
              // Title replaces the asset name only once the client approved it.
              metaEntry.episodeTitle = projData.episodeMetaConfirmedAt ? projData.episodeTitle || '' : '';
              metaEntry.episodeDescription = projData.episodeDescription || '';
              metaEntry.episodeThumbnailUrl = projData.episodeThumbnailUrl || '';
              // path lets the viewer re-sign a fresh full-res thumbnail URL at
              // render time (episodeThumbnailUrl is a Bunny-signed URL that expires).
              metaEntry.episodeThumbnailPath = projData.episodeThumbnailPath || '';
              metaEntry.isEpisodeEnriched = true;
            }
          }
          reviewMeta.value = { [reviewData.id]: metaEntry };
        } catch (metaErr) {
          logger.warn('[ClientReviewStore] Failed to build meta for single review', { error: metaErr.message });
        }

        await _initReviews([reviewData], user, companyId);
        return;
      }

      // A single-review view (the episode panel) is pinned — don't clobber its
      // scope with the hub's multi-review load, which excludes primary-episode
      // deliverables and would otherwise evict the panel's own review. The hub
      // reloads when the panel unmounts and releases its pin.
      if (_protectedReviewIds.size > 0) {
        _debugStep('Skipping multi-review init — a single review is pinned', { pinned: _protectedReviewIds.size });
        initialized.value = true;
        loading.value = false;
        return;
      }

      // ── Multi-review: fetch all reviews for this company ──
      _debugStep('Calling getActiveReviewsForCompany()');
      const allReviews = await reviewService.getActiveReviewsForCompany(companyId);

      if (allReviews.length === 0) {
        initDebug.value.exitReason = 'No reviews found — reviewService.getActiveReviewsForCompany() returned []. No "reviews" doc exists for this company.';
        _debugStep('EXIT: no reviews', { companyId });
        logger.warn('[ClientReviewStore] No reviews found for company', { companyId });
        initialized.value = true;
        loading.value = false;
        return;
      }
      _debugStep('Reviews found', { count: allReviews.length, ids: allReviews.map(r => r.id) });

      // Check each review's deliverable stage
      // Wrapped in per-review try/catch: stale reviews may reference deleted deliverables
      // which throw permission errors for 'user' type (Firestore can't evaluate
      // resource.data.companyId on a non-existent doc)
      const deliverableChecks = (await Promise.all(
        allReviews.map(async (r) => {
          const deliverableId = r.deliverableId || r.id;
          try {
            const snap = await getDoc(doc(db, 'deliverables', deliverableId));
            const data = snap.data();
            return {
              review: r,
              deliverableId,
              exists: snap.exists(),
              stage: data?.stage || null,
              name: data?.title || data?.name || null,
              isPrimaryEpisodeDeliverable: data?.isPrimaryEpisodeDeliverable || false,
              clientReviewStatus: data?.clientReviewStatus || null,
            };
          } catch (readErr) {
            _debugStep('Deliverable read failed', { deliverableId, error: readErr.message });
            return {
              review: r,
              deliverableId,
              exists: false,
              stage: null,
              name: null,
              readError: readErr.message,
            };
          }
        })
      ));

      // Admin/super users see all reviews regardless of deliverable stage
      const isAdminOrSuper = _isAdminOrSuper(user);
      // Primary-episode deliverables are reviewed in the dedicated episode page
      // (ClientEpisodeDetail), so exclude them from the generic Reviews hub for
      // clients. (Admins still see everything in the hub.)
      // Primary-episode deliverables are reviewed on their own episode page, so
      // they're always excluded from the merged hub list (admin-as-client too)
      // and surfaced as their own rows instead.
      const qualifying = isAdminOrSuper
        ? deliverableChecks.filter(d => d.exists && !d.isPrimaryEpisodeDeliverable)
        : deliverableChecks.filter(d => d.stage === 'client_review' && !d.isPrimaryEpisodeDeliverable);
      const filtered = isAdminOrSuper
        ? deliverableChecks.filter(d => !d.exists || d.isPrimaryEpisodeDeliverable)
        : deliverableChecks.filter(d => d.stage !== 'client_review' || d.isPrimaryEpisodeDeliverable);

      // Podcast episodes at client_review → one row each, routing to the episode
      // page. Computed BEFORE the no-qualifying early return so an episodes-only
      // client still sees them.
      const episodeChecks = deliverableChecks.filter(
        d => d.exists && d.stage === 'client_review' && d.isPrimaryEpisodeDeliverable
      );
      if (episodeChecks.length) {
        const [epSnaps, epItemSnaps] = await Promise.all([
          Promise.all(episodeChecks.map(d => getDoc(doc(db, 'projects', d.review.projectId)).catch(() => null))),
          // Item statuses so the hub list can show the episode's progress like any
          // other deliverable. One-time read; the episode page keeps its own listener.
          Promise.all(episodeChecks.map(d => getDocs(collection(db, 'reviews', d.review.id, 'items')).catch(() => null))),
        ]);
        pendingEpisodeReviews.value = episodeChecks
          .map((d, i) => {
            const p = epSnaps[i]?.exists() ? epSnaps[i].data() : {};
            const itemDocs = epItemSnaps[i]?.docs || [];
            const statuses = itemDocs.map(x => x.data().clientStatus);
            // Cover fallback when the episode has no thumbnail yet: the latest
            // client-facing version of the full episode (else any video item).
            const coverSource = [...itemDocs]
              .sort((a, b) => (b.data().slot === 'full_episode') - (a.data().slot === 'full_episode'))
              .map(x => {
                const v = (x.data().versions || [])
                  .filter(ver => ver.clientVersionNumber != null)
                  .sort((a, b) => b.clientVersionNumber - a.clientVersionNumber)[0];
                return v && x.data().type === 'video' ? { reviewId: d.review.id, itemId: x.id, version: v } : null;
              })
              .find(Boolean) || null;
            return {
              reviewId: d.deliverableId,
              projectId: d.review.projectId,
              showId: p.showId || null,
              title: episodeDisplayTitle(p) || 'Episode',
              episodeNumber: p.episodeNumber || null,
              thumbnailPath: p.episodeThumbnailPath || '',
              thumbnailUrl: p.episodeThumbnailUrl || '',
              itemCount: statuses.length,
              decidedCount: statuses.filter(st => !isActionableStatus(st)).length,
              approvedCount: statuses.filter(st => st === REVIEW_ITEM_STATUS.APPROVED).length,
              coverSource,
            };
          })
          .filter(e => e.showId);
      } else {
        pendingEpisodeReviews.value = [];
      }

      // Changes requested → automation moves the deliverable out of client_review,
      // which would drop it from the hub. List it under "With your editor" until it
      // comes back. Admins already get non-episode ones through `qualifying`.
      const qualifyingIds = new Set(qualifying.map(d => d.deliverableId));
      const withEditor = deliverableChecks.filter(d =>
        d.exists &&
        d.clientReviewStatus === 'changes_requested' &&
        !['client_review', 'approved', 'complete'].includes(d.stage) &&
        !qualifyingIds.has(d.deliverableId)
      );
      withEditorReviews.value = await Promise.all(withEditor.map(async (d) => {
        const [pSnap, itemSnap] = await Promise.all([
          d.review.projectId ? getDoc(doc(db, 'projects', d.review.projectId)).catch(() => null) : null,
          getDocs(collection(db, 'reviews', d.review.id, 'items')).catch(() => null),
        ]);
        const p = pSnap?.exists() ? pSnap.data() : {};
        const statuses = (itemSnap?.docs || []).map(x => x.data().clientStatus);
        const isEpisode = d.isPrimaryEpisodeDeliverable && !!p.showId;
        return {
          reviewId: d.deliverableId,
          projectId: d.review.projectId || null,
          showId: isEpisode ? p.showId : null,
          isEpisode,
          title: (isEpisode && p.episodeMetaConfirmedAt && p.episodeTitle) || d.name || p.name || 'Review',
          projectName: p.name || '',
          episodeNumber: p.episodeNumber || null,
          thumbnailPath: isEpisode ? p.episodeThumbnailPath || '' : '',
          thumbnailUrl: isEpisode ? p.episodeThumbnailUrl || '' : '',
          itemCount: statuses.length,
          approvedCount: statuses.filter(st => st === REVIEW_ITEM_STATUS.APPROVED).length,
        };
      }));

      initDebug.value.deliverables = {
        total: deliverableChecks.length,
        isAdminOrSuper,
        qualifying: qualifying.map(d => ({ id: d.deliverableId, name: d.name, stage: d.stage })),
        filtered: filtered.map(d => ({ id: d.deliverableId, name: d.name, stage: d.stage, reason: d.readError || `stage is "${d.stage}"` })),
      };
      _debugStep('Deliverable stage check', { qualifying: qualifying.length, filtered: filtered.length, isAdminOrSuper });

      if (qualifying.length === 0) {
        initDebug.value.exitReason = isAdminOrSuper
          ? `All ${deliverableChecks.length} reviews filtered out — no deliverables exist.`
          : `All ${deliverableChecks.length} reviews filtered out — none have deliverable stage "client_review". Stages found: ${[...new Set(deliverableChecks.map(d => d.stage))].join(', ')}`;
        _debugStep('EXIT: no qualifying reviews');
        // Same teardown as _initReviews' reconcile: an episode review left subscribed by
        // the episode page would otherwise list here alongside its episode row.
        for (const rid of Object.keys(_reviewItems)) {
          if (!_protectedReviewIds.has(rid)) _removeReview(rid);
        }
        initialized.value = true;
        loading.value = false;
        return;
      }

      // Set deliverableStage to indicate at least one is active
      deliverableStage.value = qualifying[0]?.stage || 'client_review';

      // Build reviewMeta: fetch project names for group headers
      const uniqueProjectIds = [...new Set(qualifying.map(d => d.review.projectId).filter(Boolean))];
      const projectDocs = await Promise.all(
        uniqueProjectIds.map(pid => getDoc(doc(db, 'projects', pid)))
      );
      const projectDataMap = {};
      projectDocs.forEach((snap, i) => {
        if (snap.exists()) {
          projectDataMap[uniqueProjectIds[i]] = snap.data();
        }
      });
      const meta = {};
      for (const d of qualifying) {
        const projectData = projectDataMap[d.review.projectId];
        const metaEntry = {
          deliverableName: d.name || '',
          projectName: projectData?.name || '',
        };

        // Enrich with episode metadata for primary episode deliverables
        if (d.isPrimaryEpisodeDeliverable && projectData?.type === 'episode') {
          metaEntry.episodeTitle = projectData.episodeMetaConfirmedAt ? projectData.episodeTitle || '' : '';
          metaEntry.episodeDescription = projectData.episodeDescription || '';
          metaEntry.episodeThumbnailUrl = projectData.episodeThumbnailUrl || '';
          // path lets the viewer re-sign a fresh full-res thumbnail URL at
          // render time (episodeThumbnailUrl is a Bunny-signed URL that expires).
          metaEntry.episodeThumbnailPath = projectData.episodeThumbnailPath || '';
          metaEntry.isEpisodeEnriched = true;
          logger.info('[ClientReviewStore] Episode enrichment applied', {
            reviewId: d.review.id,
            episodeTitle: metaEntry.episodeTitle,
            hasDescription: !!metaEntry.episodeDescription,
            hasThumbnail: !!metaEntry.episodeThumbnailUrl,
          });
        } else if (d.isPrimaryEpisodeDeliverable) {
          logger.warn('[ClientReviewStore] isPrimaryEpisodeDeliverable but project not episode', {
            reviewId: d.review.id,
            projectType: projectData?.type,
            projectId: d.review.projectId,
            hasProjectData: !!projectData,
          });
        }

        meta[d.review.id] = metaEntry;
      }
      reviewMeta.value = meta;

      await _initReviews(qualifying.map(d => d.review), user, companyId);
    } catch (err) {
      error.value = err.message;
      initDebug.value = { ...initDebug.value, exitReason: `Caught error: ${err.message}`, error: { message: err.message, stack: err.stack } };
      _debugStep('EXIT: caught error', { error: err.message });
      logger.error('[ClientReviewStore] Init failed', { error: err.message, reviewId });
    } finally {
      loading.value = false;
      // Debug summary — check browser console to see why reviews aren't showing
      logger.info('[ClientReviewStore] Init complete', {
        companyId,
        exitReason: initDebug.value.exitReason,
        userRole: initDebug.value.user?.systemRole,
        reviewsFound: initDebug.value.deliverables?.total ?? 0,
        qualifying: initDebug.value.deliverables?.qualifying?.length ?? 0,
        isAdminOrSuper: initDebug.value.deliverables?.isAdminOrSuper,
        itemCount: Object.keys(items.value).length,
        steps: initDebug.value.steps?.map(s => s.label),
      });
    }
  }

  /**
   * Subscribe to all qualifying reviews. Shared by both single-review and multi-review paths.
   */
  async function _initReviews(reviewDataArray, user, companyId) {
    const isAdminOrSuper = _isAdminOrSuper(user);

    // ponytail: no per-project subscriber gate — company membership is the access
    // boundary (enforced by firestore.rules on /reviews). Anyone in the company sees
    // every review for that company.
    const accessibleReviews = reviewDataArray;
    initDebug.value.access = { isAdminOrSuper, gate: 'company-only' };
    _debugStep('Access granted via company membership', { isAdminOrSuper });

    // Already initialized for the same set of reviews
    const newIds = new Set(accessibleReviews.map(r => r.id));

    // Reconcile: the store must be subscribed to exactly `accessibleReviews`.
    // Tear down any review no longer in the target set — e.g. the episode review
    // left subscribed when navigating episode -> Back to the hub (its listener
    // would otherwise keep merging episode items into the hub list). Runs before
    // the skip-guard below so the guard sees the corrected subscription set.
    for (const rid of Object.keys(_reviewItems)) {
      if (!newIds.has(rid) && !_protectedReviewIds.has(rid)) _removeReview(rid);
    }

    const existingIds = new Set(Object.keys(reviews.value));
    if (initialized.value && newIds.size === existingIds.size && [...newIds].every(id => existingIds.has(id))) {
      _debugStep('Already initialized for these reviews, skipping');
      return;
    }

    initDebug.value.reviews = accessibleReviews.map(r => ({
      id: r.id,
      status: r.status || null,
      deliverableId: r.deliverableId || null,
      projectId: r.projectId || null,
      companyId: r.companyId || null,
      itemCount: r.itemCount ?? null,
    }));

    // Subscribe to each qualifying review
    for (const reviewData of accessibleReviews) {
      const rid = reviewData.id;
      const deliverableId = reviewData.deliverableId || rid;

      // Subscribe to review doc
      reviewService.subscribeToReview(rid, (data) => {
        reviews.value = { ...reviews.value, [rid]: data };
      });

      // Subscribe to items — merge into per-review map, then recompute
      _reviewItems[rid] = {};
      reviewService.subscribeToItems(rid, (itemsData) => {
        if (isAdminOrSuper) {
          _reviewItems[rid] = itemsData;
        } else {
          // Filter to only client-facing versions
          const versionIds = new Set(clientFacingVersionIds.value);
          const filtered = {};

          for (const [id, item] of Object.entries(itemsData)) {
            const clientVersions = (item.versions || [])
              .filter(v => v.clientVersionNumber != null)
              .sort((a, b) => b.clientVersionNumber - a.clientVersionNumber)
              .map((v) => {
                const label = `v${v.clientVersionNumber}`;
                versionIds.add(v.id);
                return { ...v, label };
              });

            if (clientVersions.length === 0) continue;

            filtered[id] = {
              ...item,
              versions: clientVersions,
              currentVersionId: clientVersions[0].id,
              currentVersionNumber: clientVersions[0].clientVersionNumber
            };
          }

          clientFacingVersionIds.value = versionIds;
          _reviewItems[rid] = filtered;
        }

        _recomputeItems();

        // Auto-select first item if none selected
        if (!activeItemId.value && Object.keys(items.value).length > 0) {
          const sorted = Object.values(items.value).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
          activeItemId.value = sorted[0]?.id || null;
        }
      });

      // Subscribe to comments — merge into per-review map, then recompute
      _reviewComments[rid] = [];
      reviewService.subscribeToComments(rid, (commentsData) => {
        if (isAdminOrSuper) {
          _reviewComments[rid] = commentsData;
        } else {
          _reviewComments[rid] = commentsData.filter(c =>
            c.userType !== 'admin' &&
            c.userType !== 'super' &&
            clientFacingVersionIds.value.has(c.versionId)
          );
        }
        _recomputeComments();
      });

      // Subscribe to deliverable stage changes — remove this review if stage moves away
      // Admin/super users see reviews in any stage, so skip removal for them.
      // A pinned review (the episode panel) stays too: after the client's feedback
      // moves it to revision_queue the panel still needs its items to show the
      // revisions and the episode's unconfirmed copy — dropping them read as "complete".
      if (_unsubDeliverables[rid]) _unsubDeliverables[rid]();
      _unsubDeliverables[rid] = onSnapshot(doc(db, 'deliverables', deliverableId), (snap) => {
        const stage = snap.data()?.stage;
        if (stage !== 'client_review' && !isAdminOrSuper && !_protectedReviewIds.has(rid)) {
          _debugStep('Deliverable stage changed, removing review', { reviewId: rid, stage });
          _removeReview(rid);

          // If no reviews left, clear deliverableStage
          if (Object.keys(reviews.value).length === 0) {
            deliverableStage.value = stage;
          }
        }
      });
    }

    _debugStep('Subscriptions active — init complete', { reviewCount: reviewDataArray.length });
    initialized.value = true;
    logger.info('[ClientReviewStore] Initialized', { companyId, reviewCount: reviewDataArray.length, reviewIds: reviewDataArray.map(r => r.id) });
  }

  function reset() {
    reviewService.unsubscribeAll();

    // Clear all deliverable unsubscribers
    for (const rid of Object.keys(_unsubDeliverables)) {
      if (_unsubDeliverables[rid]) _unsubDeliverables[rid]();
      delete _unsubDeliverables[rid];
    }

    // Clear per-review maps
    for (const key of Object.keys(_reviewItems)) delete _reviewItems[key];
    for (const key of Object.keys(_reviewComments)) delete _reviewComments[key];

    reviews.value = {};
    items.value = {};
    comments.value = [];
    loading.value = false;
    initialized.value = false;
    error.value = null;
    activeItemId.value = null;
    activeVersionIds.value = {};
    currentCompanyId.value = null;
    clientFacingVersionIds.value = new Set();
    deliverableStage.value = null;
    reviewMeta.value = {};
    pendingEpisodeReviews.value = [];
    withEditorReviews.value = [];
    initDebug.value = { steps: [], user: null, route: null, reviews: null, deliverables: null, access: null, items: null, exitReason: null, error: null };
    _protectedReviewIds.clear();
  }

  // ─── Single-review pinning ───────────────────────────────
  // The episode review panel pins its review on mount so the hub's multi-review
  // init can't clobber it (see the guards in init/reconcile), and releases it on
  // unmount so the hub can reload normally.
  function protectReview(reviewId) {
    if (reviewId) _protectedReviewIds.add(reviewId);
  }
  function unprotectReview(reviewId) {
    if (reviewId) _protectedReviewIds.delete(reviewId);
  }

  function selectItem(itemId) {
    activeItemId.value = itemId;
  }

  function selectVersion(itemId, versionId) {
    activeVersionIds.value = { ...activeVersionIds.value, [itemId]: versionId };
  }

  // ─── Comment Actions ─────────────────────────────────────

  async function addComment(commentData) {
    const reviewId = _reviewIdForItem(commentData.itemId);
    if (!reviewId) {
      logger.warn('[ClientReviewStore] addComment: cannot resolve reviewId', { itemId: commentData.itemId });
      return;
    }
    return reviewService.addComment(reviewId, commentData);
  }

  async function addReply(parentCommentId, replyText) {
    const parent = comments.value.find(c => c.id === parentCommentId);
    if (!parent) {
      logger.warn('[ClientReviewStore] addReply: parent not found', { parentCommentId });
      return;
    }
    const reviewId = parent._reviewId || _reviewIdForItem(parent.itemId);
    if (!reviewId) {
      logger.warn('[ClientReviewStore] addReply: cannot resolve reviewId', { parentCommentId });
      return;
    }
    return reviewService.addComment(reviewId, {
      itemId: parent.itemId,
      versionId: parent.versionId,
      text: replyText,
      parentId: parentCommentId
    });
  }

  async function updateComment(commentId, updates) {
    const reviewId = _reviewIdForComment(commentId);
    if (!reviewId) {
      logger.warn('[ClientReviewStore] updateComment: cannot resolve reviewId', { commentId });
      return;
    }
    return reviewService.updateComment(reviewId, commentId, updates);
  }

  async function deleteComment(commentId) {
    const reviewId = _reviewIdForComment(commentId);
    if (!reviewId) {
      logger.warn('[ClientReviewStore] deleteComment: cannot resolve reviewId', { commentId });
      return;
    }
    return reviewService.deleteComment(reviewId, commentId);
  }

  async function toggleResolved(commentId, resolved) {
    const reviewId = _reviewIdForComment(commentId);
    if (!reviewId) {
      logger.warn('[ClientReviewStore] toggleResolved: cannot resolve reviewId', { commentId });
      return;
    }
    const userId = userAuthStore.user.id;

    // Optimistic local update for instant UI feedback
    comments.value = comments.value.map(c =>
      c.id === commentId
        ? { ...c, resolved, resolvedBy: resolved ? userId : null, resolvedAt: resolved ? new Date() : null }
        : c
    );

    return reviewService.toggleCommentResolved(reviewId, commentId, resolved, userId);
  }

  // ─── Status Actions ─────────────────────────────────────

  async function approveItem(itemId, assetMeta = {}) {
    const reviewId = _reviewIdForItem(itemId);
    if (!reviewId) {
      logger.warn('[ClientReviewStore] approveItem: cannot resolve reviewId', { itemId });
      return;
    }
    const userId = userAuthStore.user.id;

    // Optimistic local update
    if (items.value[itemId]) {
      items.value = {
        ...items.value,
        [itemId]: {
          ...items.value[itemId],
          clientStatus: REVIEW_ITEM_STATUS.APPROVED,
          clientApprovedBy: userId,
          clientApprovedAt: new Date()
        }
      };
    }

    // Pass approval asset metadata so the Cloud Function can create the asset
    const extraUpdates = {};
    if (assetMeta.name) extraUpdates.approvalAssetName = assetMeta.name.trim();
    if (assetMeta.assetType) extraUpdates.approvalAssetType = assetMeta.assetType;
    if (assetMeta.tags) extraUpdates.approvalTags = assetMeta.tags;

    return reviewService.updateItemStatus(reviewId, itemId, 'client', REVIEW_ITEM_STATUS.APPROVED, userId, extraUpdates);
  }

  async function approveForFinalization(itemId) {
    const reviewId = _reviewIdForItem(itemId);
    if (!reviewId) {
      logger.warn('[ClientReviewStore] approveForFinalization: cannot resolve reviewId', { itemId });
      return;
    }
    const userId = userAuthStore.user.id;

    // Optimistic local update
    if (items.value[itemId]) {
      items.value = {
        ...items.value,
        [itemId]: {
          ...items.value[itemId],
          clientStatus: REVIEW_ITEM_STATUS.PENDING_FINALIZATION,
          finalizationStatus: FINALIZATION_STATUS.PENDING,
          clientApprovedBy: userId,
          clientApprovedAt: new Date(),
          internalStatus: REVIEW_ITEM_STATUS.IN_REVISION,
          internalApprovedBy: null,
          internalApprovedAt: null
        }
      };
    }

    return reviewService.approveForFinalization(reviewId, itemId, userId);
  }

  async function requestChanges(itemId) {
    const reviewId = _reviewIdForItem(itemId);
    if (!reviewId) {
      logger.warn('[ClientReviewStore] requestChanges: cannot resolve reviewId', { itemId });
      return;
    }
    const userId = userAuthStore.user.id;

    // Optimistic local update
    if (items.value[itemId]) {
      items.value = {
        ...items.value,
        [itemId]: {
          ...items.value[itemId],
          clientStatus: REVIEW_ITEM_STATUS.CHANGES_REQUESTED,
          clientApprovedBy: null,
          clientApprovedAt: null,
          // Cascade: reset internal to in_revision so item must be re-reviewed internally
          internalStatus: REVIEW_ITEM_STATUS.IN_REVISION,
          internalApprovedBy: null,
          internalApprovedAt: null
        }
      };
    }

    return reviewService.updateItemStatus(reviewId, itemId, 'client', REVIEW_ITEM_STATUS.CHANGES_REQUESTED, userId, {
      changesRequestedAtVersion: items.value[itemId]?.currentVersionNumber || 1
    });
  }

  async function removeItem(itemId) {
    const reviewId = _reviewIdForItem(itemId);
    if (!reviewId) {
      logger.warn('[ClientReviewStore] removeItem: cannot resolve reviewId', { itemId });
      return;
    }

    // Remove from local state immediately
    const { [itemId]: _removed, ...remaining } = items.value;
    items.value = remaining;

    // Delete from Firestore (files, comments, and item doc)
    await reviewService.deleteItem(reviewId, itemId, null, 'client_post_download');

    logger.info('[ClientReviewStore] Item removed after download', { reviewId, itemId });
  }

  return {
    // State
    reviews,
    review,
    items,
    comments,
    loading,
    initialized,
    error,
    activeItemId,
    activeVersionIds,
    currentCompanyId,
    deliverableStage,
    reviewMeta,
    pendingEpisodeReviews,
    withEditorReviews,
    initDebug,

    // Getters
    sortedItems,
    actionableItems,
    allItemsActioned,
    activeItem,
    activeVersion,
    itemComments,
    unresolvedCommentCount,
    reviewProgress,
    firstReviewId,
    reviewIdForItem: _reviewIdForItem,

    // Actions
    init,
    reset,
    protectReview,
    unprotectReview,
    selectItem,
    selectVersion,
    addComment,
    addReply,
    updateComment,
    deleteComment,
    toggleResolved,
    approveItem,
    approveForFinalization,
    requestChanges,
    removeItem
  };
});
